import React, { useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Paper,
  Typography,
} from "@mui/material";
import { CommandButton } from "./CommandButton";
import { ReactComponent as SmartRTL } from "../assets/svg/SmartRTL.svg";
import { ReactComponent as Close } from "../assets/svg/Close.svg";
import { ReactComponent as DisconnectIcon } from "../assets/svg/Disconnect.svg";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { issueCommandThunk, resetConnections, abortFleetInCellThunk } from "../features/aircraft/aircraftSlice";
import { selectCell } from "../features/session/sessionSlice";
import { SlideToConfirm } from "./SlideToConfirm";
import { commandState } from "./ConfirmCommand";
import { Command } from "../features/aircraft/types";
import {
  connectedToCraft,
  selectAvailableCraft,
  selectAvailableCraftAuto,
} from "../features/aircraft/aircraftSelectors";
import { Websockets } from "../features/aircraft/Websockets";
const DisconnectIconDark = require("../assets/svg/DisconnectDark.png");

export const CommandTrayAll = () => {
  const [command, setCommand] = useState<Command | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [disconnectLoading, setDisconnectLoading] = useState<boolean>(false);
  const [isDisconnectMode, setIsDisconnectMode] = useState<boolean>(false);
  const [readyToRelease, setReadyToRelease] = useState<boolean>(false);
  const aircraftIds = useAppSelector(selectAvailableCraft);
  const isConnectedToCraft = useAppSelector(connectedToCraft);
  const aircraftIdsAutoOn = useAppSelector(selectAvailableCraftAuto);
  const cell = useAppSelector(selectCell);
  const dispatch = useAppDispatch();

  const getReleaseLabel = (action: string) => {
    if (action.startsWith("Slide To ")) {
      return action.replace("Slide To ", "Release to ");
    }
    if (action.startsWith("Slide to ")) {
      return action.replace("Slide to ", "Release to ");
    }
    return `Release to ${action}`;
  };

  const updateCommand = (newCommand: Command) => {
    if (newCommand !== null && newCommand === command) {
      setCommand(null);
    } else {
      setCommand(newCommand);
    }
    setReadyToRelease(false);
    setIsDisconnectMode(false);
  };

  const smartRtl = () => {
    updateCommand("COMMAND_SMARTRTL");
  };

  const abort = () => {
    updateCommand("COMMAND_ABORT");
  };

  const auto = () => {
    updateCommand("COMMAND_AUTO_ARM");
  };

  const disconnectAll = () => {
    setIsDisconnectMode(!isDisconnectMode);
    setCommand(null);
    setReadyToRelease(false);
  };

  const clearPendingAction = () => {
    setCommand(null);
    setIsDisconnectMode(false);
    setReadyToRelease(false);
  };

  const handleClickAway = () => {
    if (loading || disconnectLoading) {
      return;
    }
    clearPendingAction();
  };

  const performDisconnectAll = async () => {
    try {
      setDisconnectLoading(true);
      dispatch(resetConnections());
      Websockets.getInstance().disconnectAllUavs();
      setIsDisconnectMode(false);
      setReadyToRelease(false);
      setDisconnectLoading(false);
    } catch (err) {
      setDisconnectLoading(false);
      setIsDisconnectMode(false);
      console.log(err);
    }
  };

  const issueCommand = async () => {
    try {
      if (command) {
        setLoading(true);

        if (command === "COMMAND_ABORT") {
          await dispatch(abortFleetInCellThunk()).unwrap();
        } else {
          const idsToUse =
            command === "COMMAND_AUTO_ARM" ? aircraftIdsAutoOn : aircraftIds;

          const promises = idsToUse.map((id) =>
            dispatch(issueCommandThunk({ uavId: id, command })).unwrap()
          );
          await Promise.all(promises);
        }

        setCommand(null);
        setReadyToRelease(false);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setCommand(null);
      console.log(err);
    }
  };
  if (!isConnectedToCraft) return null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          width: 176,
          position: "relative",
          pointerEvents: "all",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            borderRadius: 4,
            overflow: "hidden",
            px: 2,
            py: 2,
          }}
        >
          <Typography variant="h5" fontWeight={"600"} textAlign="center">
            All Aircraft
          </Typography>
          <Box
            pt={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Box display="flex">
              {cell?.id === 12 || cell?.id === 13 ? (
                <CommandButton
                  text="SRTL"
                  icon={SmartRTL}
                  onClick={smartRtl}
                  loading={command === "COMMAND_SMARTRTL" && loading}
                  active={command === "COMMAND_SMARTRTL"}
                  ripple={command === "COMMAND_SMARTRTL"}
                  disableWhenActive={false}
                  style={{ borderColor: "red", backgroundColor: "red" }}
                />
              ) : (
                <CommandButton
                  text="Abort"
                  icon={SmartRTL}
                  onClick={abort}
                  loading={command === "COMMAND_ABORT" && loading}
                  active={command === "COMMAND_ABORT"}
                  ripple={command === "COMMAND_ABORT"}
                  disableWhenActive={false}
                />
              )}
            </Box>
            <Box display="flex">
              <CommandButton
                text="Auto Off"
                icon={Close}
                onClick={auto}
                loading={command === "COMMAND_AUTO_ARM" && loading}
                active={command === "COMMAND_AUTO_ARM"}
                ripple={command === "COMMAND_AUTO_ARM"}
                disableWhenActive={false}
              />
            </Box>
            <Box display="flex">
              <CommandButton
                text="Disconnect"
                icon={DisconnectIcon}
                onClick={disconnectAll}
                loading={isDisconnectMode && disconnectLoading}
                active={isDisconnectMode}
                ripple={isDisconnectMode}
                disableWhenActive={false}
              />
            </Box>
          </Box>
        </Paper>
        <Collapse
          in={!!command || isDisconnectMode}
          unmountOnExit
          sx={{
            position: "absolute",
            top: "calc(100% + 16px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: 176,
          }}
        >
          <Paper
            sx={{
              width: "100%",
              borderRadius: 4,
              px: 2,
              py: 2,
              backgroundColor: "secondary.light",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              textAlign="center"
              sx={{ minHeight: 40 }}
            >
              {command
                ? commandState?.[command]?.description
                : isDisconnectMode
                  ? "All aircraft will be disconnected"
                  : ""}
            </Typography>
            <Typography
              sx={{
                textJustify: "center",
                textAlign: "center",
              }}
              variant={"body2"}
              fontWeight={"800"}
            >
              {readyToRelease
                ? command
                  ? getReleaseLabel(commandState[command].action)
                  : isDisconnectMode
                    ? "Release to Disconnect"
                    : ""
                : command
                  ? commandState?.[command]?.action
                  : isDisconnectMode
                    ? "Slide down to disconnect"
                    : ""}
            </Typography>
            <SlideToConfirm
              style={{ height: 200, width: 64 }}
              onConfirm={command ? issueCommand : performDisconnectAll}
              onReadyChange={setReadyToRelease}
              icon={
                command
                  ? commandState?.[command]?.icon
                  : isDisconnectMode
                    ? DisconnectIconDark
                    : ""
              }
              warning={command === "COMMAND_SMARTRTL"}
              ripple
              orientation="vertical"
            >
              {null}
            </SlideToConfirm>
            <Button
              fullWidth
              sx={{ height: 48 }}
              variant="outlined"
              onClick={clearPendingAction}
              disabled={loading || disconnectLoading}
            >
              Cancel
            </Button>
          </Paper>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
};
